import React, { useEffect, useState } from "react";
import Dropdown from "react-bootstrap/Dropdown";
import { Link, useLocation } from "react-router-dom";
import useAgentGitchaRestrictions from "@/hooks/useAgentGitchaRestrictions";
import { getLocalStorage } from "@/helpers/common";
import { USER_INFO_KEY } from "@/constants/common";
import { hasBlsHost, openInBls } from "@/helpers/crossLoginHelper";
import "./BlsHandoff.scss";

export const ACCOUNT_PATHS = [
  "/settings",
  "/messages",
  "/direct-messages",
  "/my-want-listings",
  "/my-inventory",
  "/my-clients",
  "/all-introductions",
  "/view-all-introductions",
  "/view-introduced-property",
  "/chat",
  "/agent-dashboard",
  "/want-listing-creation",
  "/create-want-listing-agent",
  "/edit-want-listing-agent",
  "/create-inventory",
];

export const isAccountPath = (pathname) =>
  ACCOUNT_PATHS.some((path) => pathname === path || pathname.startsWith(`${path}/`));

const mlsOfSession = () => getLocalStorage(USER_INFO_KEY)?.userInfo?.mls;

export const blsNameFor = (mls) => {
  const slug = String(mls || "").trim();
  if (!slug || slug === "default") return "";
  return slug.length <= 4 ? slug.toUpperCase() : slug.charAt(0).toUpperCase() + slug.slice(1);
};

export const useSendsAgentToBls = () => {
  const { shouldRestrictAgent } = useAgentGitchaRestrictions();
  return Boolean(shouldRestrictAgent && hasBlsHost(mlsOfSession()));
};

export const useBlsHandoff = () => {
  const { pathname, search } = useLocation();
  const sendsToBls = useSendsAgentToBls();
  const mls = mlsOfSession();
  const handingOff = Boolean(sendsToBls && isAccountPath(pathname));

  useEffect(() => {
    if (handingOff) openInBls(mls, `${pathname}${search}`);
  }, [handingOff, mls, pathname, search]);

  return handingOff;
};

export const BlsHandoffNotice = () => (
  <div className="bls-handoff" role="status">
    <div className="bls-handoff__card">
      <span className="bls-handoff__spinner" aria-hidden="true" />
      <h1>Opening your {blsNameFor(mlsOfSession())} Buyer Listing Service</h1>
      <p>Your account, messages and Buyer Want-Listings live in your MLS&apos;s Buyer Listing Service. You&apos;ll be signed in there&nbsp;automatically.</p>
    </div>
  </div>
);

export const BlsHomeLink = ({ className = "", variant = "header" }) => {
  const sendsToBls = useSendsAgentToBls();
  const [opening, setOpening] = useState(false);
  const mls = mlsOfSession();
  if (!sendsToBls) return null;
  return (
    <button
      type="button"
      className={`bls-home-link bls-home-link--${variant} ${className}`.trim()}
      disabled={opening}
      onClick={() => {
        setOpening(true);
        openInBls(mls, "/home");
      }}
    >
      {opening ? "Opening..." : `Open your ${blsNameFor(mls)} Buyer Listing Service`}
    </button>
  );
};

const BLS_MENU = [
  { label: "Settings and profile", path: "/settings" },
  { label: "My Want-Listings", path: "/my-want-listings" },
  { label: "My Properties", path: "/my-inventory" },
  { label: "Messages", path: "/direct-messages" },
];

const OpensElsewhere = () => (
  <svg className="bls-agent-menu__out" width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M14 4h6v6M20 4l-9 9M18 14v5a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1h5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

export const BlsAgentMenuItems = ({ onLogOut, showHome = false }) => {
  const mls = mlsOfSession();
  const name = blsNameFor(mls);
  return (
    <>
      <p className="bls-agent-menu__heading">In your {name} Buyer Listing&nbsp;Service</p>
      <p className="bls-agent-menu__note">Your account lives there. We&apos;ll sign you&nbsp;in.</p>
      {showHome ? (
        <div className="bls-agent-menu__home">
          <BlsHomeLink variant="menu" />
        </div>
      ) : null}
      {BLS_MENU.map((item) => (
        <Dropdown.Item key={item.path} className="bls-agent-menu__item" onClick={() => openInBls(mls, item.path)}>
          <span>{item.label}</span>
          <OpensElsewhere />
        </Dropdown.Item>
      ))}
      <Dropdown.Divider />
      <p className="bls-agent-menu__heading">On Gitcha.com</p>
      <Dropdown.Item as={Link} className="bls-agent-menu__item" to="/agents">
        <span>Agent directory</span>
      </Dropdown.Item>
      <Dropdown.Item className="bls-agent-menu__item" href="https://www.gitcha.com/help">
        <span>Help</span>
      </Dropdown.Item>
      <Dropdown.Divider />
      <Dropdown.Item className="bls-agent-menu__item" onClick={() => onLogOut?.()}>
        <span>Log Out</span>
      </Dropdown.Item>
    </>
  );
};
